'use client';

import React from 'react';
import { Box } from '@mantine/core';
import { RootBox } from '@/shared/RootBox';
import { SectionTitle } from '@/shared/SectionTitle';
import { motion } from 'framer-motion';
import Brackets from './brackets.svg';
import { SectionSubtitle } from '@/shared/SectionSubtitle';

type PyramidStepProps = {
  title: React.ReactNode;
  description: React.ReactNode;
  widthClass: string;
};

const PyramidStep = ({ title, description, widthClass }: PyramidStepProps) => {
  return (
    <div
      className={`mx-auto min-h-[130px] justify-center items-center flex flex-col w-full ${widthClass} rounded-2xl border border-black/10 bg-lightBlue-200/40 px-8 py-6 text-center shadow-md shadow-zinc-200/70 backdrop-blur-sm dark:border-white/10 dark:bg-secondary-500/10 xxs:max-md:px-5 xxs:max-md:py-5`}
    >
      <div className="font-montserrat text-2xl font-bold tracking-wide text-primary-500 xxs:max-md:text-xl">
        {title}
      </div>
      <div className="mt-2 text-sm leading-relaxed text-secondary-500 dark:text-lightBlue-200/90">
        {description}
      </div>
    </div>
  );
};

const mobileGroups = [
  {
    title: 'Preselection',
    subtitle: (
      <>
        <span className="text-primary-500">(automated)</span> — Quarterly top 20 companies
      </>
    ),
    steps: [
      {
        title: <span className="text-3xl xxs:max-md:text-2xl">38K+</span>,
        description: (
          <>
            <span className="font-semibold">All GLS*</span> companies sourced from PitchBook &amp;
            verified institutional data
          </>
        ),
      },
      {
        title: 'Sort & Eliminate',
        description: (
          <>
            Preliminary score and sort by projected revenue growth
            <br />
            Eliminate companies with: low revenue; qualitative parameters of investment attractiveness
          </>
        ),
      },
      {
        title: 'TOP 20 Preliminary Scoring',
        description: <>Quarterly 20 Best Companies</>,
      },
    ],
  },
  {
    title: 'Final selection',
    subtitle: (
      <>
        <span className="text-primary-500">(semi-automated with human-in-the-loop)</span> — Quarterly 5 best of 20
        companies
      </>
    ),
    steps: [
      {
        title: 'Deep Research',
        description: <>Automated &amp; standardized investment analysis for each company</>,
      },
      {
        title: 'Pre-source & Diversify',
        description: (
          <>
            Preliminary sourcing using our partners network
            <br />
            Portfolio diversification by country/industry/technology etc
          </>
        ),
      },
      {
        title: 'TOP 5',
        description: <>Quarterly 5 Best Companies</>,
      },
    ],
  },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const braceVariants = {
  hidden: { opacity: 0, x: 10 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

type BraceGroupProps = {
  title: string;
  subtitle: React.ReactNode;
};

const BraceGroup = ({ title, subtitle }: BraceGroupProps) => {
  return (
    <div className="flex h-full items-stretch gap-6">
      <Brackets className="h-full w-6 shrink-0 text-secondary-600/70 dark:text-lightBlue-200/50" />
      <div className="flex flex-col justify-center">
        <div className="text-lg font-semibold text-secondary-600 dark:text-lightBlue-200">
          {title}
        </div>
        <div className="mt-1 text-lg leading-snug text-secondary-600 dark:text-lightBlue-200/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
};

export const SelectionProcess = () => {
  return (
    <Box className="relative z-0 w-full" id="selectionprocess">
      <RootBox>
        <SectionTitle title="OUR SELECTION PROCESS" index={4} />
        <SectionSubtitle subtitle="From raw data to IC-ready insights in 6 steps" />

        <div className="mt-12 grid grid-cols-1 gap-y-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-x-16 lg:gap-y-8 xxs:max-lg:hidden">
          {/* Group 1 */}
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[980px]"
                title={<span className="text-3xl xxs:max-md:text-2xl">38K+</span>}
                description={
                  <>
                    <span className="font-semibold">All GLS*</span> companies
                    sourced from PitchBook &amp; verified institutional data
                  </>
                }
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[860px]"
                title="Sort & Eliminate"
                description={
                  <>
                    Preliminary score and sort by projected revenue growth
                    <br />
                    Eliminate companies with: low revenue; qualitative parameters
                    of investment attractiveness
                  </>
                }
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[780px]"
                title="TOP 20 Preliminary Scoring"
                description={<>Quarterly 20 Best Companies</>}
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="hidden lg:block"
            variants={braceVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            <BraceGroup
              title="Preselection"
              subtitle={
                <>
                  <span className="text-primary-500">(automated)</span>
                  <br />
                  Quarterly top 20 companies
                </>
              }
            />
          </motion.div>

          {/* Group 2 */}
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[720px]"
                title="Deep Research"
                description={
                  <>
                    Automated &amp; standardized investment analysis for each
                    company
                  </>
                }
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[660px]"
                title="Pre-source & Diversify"
                description={
                  <>
                    Preliminary sourcing using our partners network
                    <br />
                    Portfolio diversification by country/industry/technology etc
                  </>
                }
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <PyramidStep
                widthClass="max-w-[560px]"
                title="TOP 5"
                description={<>Quarterly 5 Best Companies</>}
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="hidden lg:block"
            variants={braceVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            <BraceGroup
              title="Final selection"
              subtitle={
                <>
                  <span className="text-primary-500">
                    (semi-automated with human-in-the-loop)
                  </span>
                  <br />
                   Quarterly 5 best of 20 companies.
                </>
              }
            />
          </motion.div>
        </div>

        {/* Mobile grouped view */}
        <div className="mt-10 flex flex-col gap-8 lg:hidden">
          {mobileGroups.map((group) => (
            <div key={group.title} className="flex flex-col gap-4 rounded-2xl border border-black/10 bg-white/50 p-6 dark:border-white/10 dark:bg-secondary-500/10">
              <div>
                <div className="text-lg font-semibold text-secondary-600 dark:text-lightBlue-200">{group.title}</div>
                <div className="mt-1 text-base leading-snug text-secondary-500 dark:text-lightBlue-200/80">
                  {group.subtitle}
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {group.steps.map((step) => (
                  <PyramidStep key={step.title?.toString()} title={step.title} description={step.description} widthClass="w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </RootBox>
    </Box>
  );
};

SelectionProcess.displayName = 'SelectionProcess';
